/*********************************************************************
 *  openfoam.adapter.js
 *
 *  – astToBlocklyXml(ast)              : Chevrotain-AST → <xml>
 *  – blocklyXmlToAst(xmlDom)           : <xml> → Chevrotain-AST-like JSON
 *  – generateFoam(ast)                 : AST → formatted OpenFOAM text
 *  – convertTextToBlocks(text, dictName, workspace) : OpenFOAM text → Blockly blocks
 *
 *  Incremental performance is achieved with a WeakMap cache that
 *  remembers the XML <block> node already generated for each AST node
 *  pointer. When only a subtree changes, we rebuild only that branch
 *  and splice it back into the existing XML DOM, instead of regenerating
 *  the whole file.
 *********************************************************************/

import * as Blockly from 'blockly';
import 'blockly/javascript';
import { dictRegistry } from './openfoam.blocks.js';
import prettierFoam, { logAstTree, prettyPrintAst } from './prettyFoam.js';      // tiny formatter
import { convertOpenFoamToBlocks } from './openfoam.text-to-blocks.js';
import { incrementalParse } from '../dsl-engine/openfoam.engine.js';

/* ---------- helpers ------------------------------------------------ */

const ast2xmlCache = new WeakMap();

/** Create a <block> node once, or retrieve it from cache */
function cachedBlock(astNode, factoryFn) {
  if (ast2xmlCache.has(astNode)) return ast2xmlCache.get(astNode);
  const block = factoryFn();
  ast2xmlCache.set(astNode, block);
  return block;
}

/** Utility to build <field>, <value>, etc. */
function blockField(tag, name, contents) {
  const elem = document.createElement(tag);
  elem.setAttribute('name', name);
  if (typeof contents === 'string') elem.textContent = contents;
  else if (contents) elem.appendChild(contents);
  return elem;
}

/* ---------- AST ➜ Blockly XML -------------------------------------- */

export function astToBlocklyXml(ast) {
  // Log the AST tree structure
  logAstTree(ast, 'Converting AST to Blockly XML');
  
  const dom = document.implementation.createDocument(null, 'xml');
  dom.documentElement.appendChild(astDictToBlock(ast, dom));
  return dom;
}

/**
 * Convert AST entry to Blockly block
 * @param {Object} entry - Entry AST node
 * @param {Document} dom - XML DOM
 * @returns {Element} Blockly block XML element
 */
function astEntryToBlock(entry, dom) {
  if (entry.type === 'keyValue') {
    return astKeyValueToBlock(entry, dom);
  } else if (entry.type === 'dictionary') {
    return astDictToBlock(entry, dom);
  } else if (entry.type === 'section') {
    return astSectionToBlock(entry, dom);
  } else {
    console.warn('Unknown entry type:', entry);
    return null;
  }
}

/**
 * Convert AST dictionary to Blockly block
 * @param {Object} dictAst - Dictionary AST node
 * @param {Document} dom - XML DOM
 * @returns {Element} Blockly block XML element
 */
function astDictToBlock(dictAst, dom) {
  const block = dom.createElement('block');
  block.setAttribute('type', 'common_dict');
  
  // Add dictionary name field
  if (dictAst.name) {
    const nameField = dom.createElement('field');
    nameField.setAttribute('name', 'DICT_NAME');
    nameField.textContent = dictAst.name;
    block.appendChild(nameField);
  }
  
  // Add entries
  if (dictAst.entries && dictAst.entries.length > 0) {
    const entriesStatement = dom.createElement('statement');
    entriesStatement.setAttribute('name', 'ENTRIES');
    
    let previousBlock = null;
    dictAst.entries.forEach(entry => {
      const entryBlock = astEntryToBlock(entry, dom);
      
      if (previousBlock) {
        const nextElement = dom.createElement('next');
        nextElement.appendChild(entryBlock);
        previousBlock.appendChild(nextElement);
      } else {
        entriesStatement.appendChild(entryBlock);
      }
      
      previousBlock = entryBlock;
    });
    
    block.appendChild(entriesStatement);
  }
  
  return block;
}

function astKeyValueToBlock(astKV, dom) {
  return cachedBlock(astKV, () => {
    const block = dom.createElement('block');
    block.setAttribute('type', 'openfoam_keyValue');
    block.appendChild(blockField('field', 'KEY', astKV.key));

    const valInput = dom.createElement('value');
    valInput.setAttribute('name', 'VAL');
    const valBlock = dom.createElement('block');
    valBlock.setAttribute('type', typeof astKV.value === 'number' ? 'math_number' : 'text');
    valBlock.appendChild(
      blockField(
        'field',
        typeof astKV.value === 'number' ? 'NUM' : 'TEXT',
        String(astKV.value)
      )
    );
    valInput.appendChild(valBlock);
    block.appendChild(valInput);
    return block;
  });
}

function astSectionToBlock(secAst, dom) {
  return cachedBlock(secAst, () => {
    const block = dom.createElement('block');
    block.setAttribute('type', `openfoam_${secAst.kind}`); // e.g. vertices / blocks

    // tuples or sub-values become child statements
    let prev = null;
    secAst.tuples.forEach(tuple => {
      const tupleBlock = astTupleToBlock(tuple, dom);
      if (prev) {
        prev.appendChild(linkElem(dom, 'next', tupleBlock));
      } else {
        block.appendChild(linkElem(dom, 'statement', tupleBlock, 'LIST'));
      }
      prev = tupleBlock;
    });
    return block;
  });
}

function astTupleToBlock(tupleAst, dom) {
  return cachedBlock(tupleAst, () => {
    const block = dom.createElement('block');
    block.setAttribute('type', 'openfoam_tuple');
    tupleAst.items.forEach((v, idx) =>
      block.appendChild(blockField('field', `I${idx}`, String(v)))
    );
    return block;
  });
}

function linkElem(dom, tag, child, name) {
  const elem = dom.createElement(tag);
  if (name) elem.setAttribute('name', name);
  elem.appendChild(child);
  return elem;
}

/* ---------- Blockly XML ➜ AST ------------------------------------- */

export function blocklyXmlToAst(xmlDom) {
  const rootBlock = xmlDom.querySelector('block');
  const ast = blockToAst(rootBlock);
  
  // Log the generated AST
  logAstTree(ast, 'Generated AST from Blockly XML');
  
  return ast;
}

function blockToAst(block) {
  const type = block.getAttribute('type');

  switch (type) {
    case 'openfoam_dictionary': {
      const name = block.querySelector('field[name="DICT_NAME"]').textContent;
      const entries = [];
      const sections = [];

      walkStatements(block, child => {
        const ast = blockToAst(child);
        if (ast.kind) sections.push(ast);
        else entries.push(ast);
      });

      return { type: 'dictionary', name, entries, sections };
    }

    case 'openfoam_keyValue': {
      const key = block.querySelector('field[name="KEY"]').textContent;
      const valBlock = block.querySelector('value[name="VAL"] block');
      const value =
        valBlock.getAttribute('type') === 'math_number'
          ? Number(valBlock.querySelector('field').textContent)
          : valBlock.querySelector('field').textContent;
      return { type: 'keyValue', key, value };
    }

    case 'openfoam_vertices':
    case 'openfoam_blocks':
    case 'openfoam_boundaries': {
      const kind = type.replace('openfoam_', '');
      const tuples = [];
      walkStatements(block, child => tuples.push(blockToAst(child)));
      return { type: 'section', kind, tuples };
    }

    case 'openfoam_tuple': {
      const items = Array.from(block.querySelectorAll('field')).map(f =>
        isNaN(f.textContent) ? f.textContent : Number(f.textContent)
      );
      return { type: 'tuple', items };
    }

    case 'common_tuple': {
      const name = block.querySelector('field[name="TUPLE_NAME"]').textContent;
      const items = [];
      
      // Extract items
      const itemFields = block.querySelectorAll('value[name^="ITEM"]');
      itemFields.forEach(itemField => {
        const valueBlock = itemField.querySelector('block');
        if (valueBlock) {
          const valueField = valueBlock.querySelector('field[name="VALUE"]');
          if (valueField) {
            const value = valueField.textContent;
            
            // Convert to number if possible
            if (valueBlock.getAttribute('type') === 'common_value_number') {
              items.push(Number(value));
            } else {
              items.push(value);
            }
          }
        }
      });
      
      return { type: 'tuple', name, items };
    }

    // Handle substitution statements
    case 'common_substitution_statement': {
      const variableField = block.querySelector('field[name="VARIABLE"]');
      const variableName = variableField ? variableField.textContent : 'p';
      
      return { 
        type: 'substitution', 
        value: `$${variableName}`,
        variableName: variableName
      };
    }

    default:
      console.warn('Unknown block type ↘ AST:', type);
      return {};
  }
}

function walkStatements(parentBlock, cb) {
  let stmt = parentBlock.querySelector('statement[name], next');
  while (stmt) {
    const childBlock = stmt.querySelector('block');
    if (childBlock) cb(childBlock);
    stmt = stmt.querySelector('next');
  }
}

/* ---------- AST ➜ OpenFOAM text ----------------------------------- */

export function generateFoam(ast) {
  // Log the AST before generating text
  logAstTree(ast, 'Generating OpenFOAM text from AST');
  
  return prettierFoam(ast); // simple pretty-printer (keys aligned, etc.)
}


/**
 * Parse OpenFOAM text to AST
 * @param {string} text - OpenFOAM dictionary text
 * @returns {Object} AST representation
 */
export function parseOpenFoamToAst(text) {
  try {
    // First try using the Chevrotain parser
    const cst = incrementalParse(text);
    console.log('Parsed CST:', cst);
    
    // Convert CST to AST
    const ast = convertCstToAst(cst);
    
    // Log the AST
    logAstTree(ast, 'Converted CST to AST');
    
    return ast;
  } catch (error) {
    console.error('Error parsing OpenFOAM text to AST with Chevrotain parser:', error);
    
    // Fallback to the simple parser
    try {
      return parseOpenFoamToJsonSimple(text);
    } catch (fallbackError) {
      console.error('Error parsing OpenFOAM text with fallback parser:', fallbackError);
      return { entries: [], sections: [], tuples: [] };
    }
  }
}

/**
 * Convert CST to AST
 * @param {Object} cst - CST from Chevrotain parser
 * @returns {Object} AST object
 */
function convertCstToAst(cst) {
  const ast = {
    type: 'dictionary',
    name: '',
    entries: [],
    sections: [],
    tuples: []
  };
  
  if (!cst || !cst.children) {
    return ast;
  }
  
  // Process statements
  if (cst.children.statement) {
    cst.children.statement.forEach(stmt => {
      // Process dictionaries
      if (stmt.children && stmt.children.dictionary) {
        const dict = stmt.children.dictionary[0];
        if (dict && dict.children) {
          let dictName = '';
          
          // Get dictionary name
          if (dict.children.FoamFile) {
            dictName = dict.children.FoamFile[0].image;
          } else if (dict.children.Name) {
            dictName = dict.children.Name[0].image;
          }
          
          // Process dictionary entries
          const entries = [];
          if (dict.children.statement) {
            dict.children.statement.forEach(entry => {
              const processedEntry = processStatementEntry(entry);
              if (processedEntry) {
                entries.push(processedEntry);
              }
            });
          }
          
          // Add dictionary to AST
          if (dictName === 'FoamFile') {
            // Special case for FoamFile
            ast.foamFile = {
              type: 'dictionary',
              name: dictName,
              entries: entries
            };
          } else {
            ast.entries.push({
              key: dictName,
              value: {
                type: 'dictionary',
                name: dictName,
                entries: entries
              }
            });
          }
        }
      }
      
      // Process key-value pairs
      else if (stmt.children && stmt.children.dictionary) {
        const keyValue = stmt.children.dictionary[0];
        if (keyValue && keyValue.children) {
          let key = '';
          
          // Get key
          if (keyValue.children.Name) {
            key = keyValue.children.Name[0].image;
          } else {
            // Try to get key from reserved keywords
            Object.keys(keyValue.children).forEach(tokenType => {
              if (tokenType !== 'value' && tokenType !== 'Semi') {
                key = keyValue.children[tokenType][0].image;
              }
            });
          }
          
          // Get value
          let value = null;
          if (keyValue.children.value) {
            value = processValue(keyValue.children.value[0]);
          }
          
          // Add key-value to AST
          if (key) {
            ast.entries.push({ key, value });
          }
        }
      }
      
      // Process tuples
      else if (stmt.children && stmt.children.tuple) {
        const tuple = stmt.children.tuple[0];
        if (tuple && tuple.children) {
          const tupleName = tuple.children.Name ? tuple.children.Name[0].image : '';
          const items = [];
          
          // Process tuple items
          if (tuple.children.tupleItem) {
            tuple.children.tupleItem.forEach(item => {
              if (item.children) {
                if (item.children.IntegerLiteral) {
                  items.push(Number(item.children.IntegerLiteral[0].image));
                } else if (item.children.FloatLiteral) {
                  items.push(Number(item.children.FloatLiteral[0].image));
                } else if (item.children.StringLiteral) {
                  items.push(item.children.StringLiteral[0].image.replace(/^"|"$/g, ''));
                } else if (item.children.Name) {
                  items.push(item.children.Name[0].image);
                }
              }
            });
          }
          
          // Add tuple to AST
          ast.tuples.push({
            type: 'tuple',
            name: tupleName,
            items: items
          });
        }
      }
      
      // Process block sections
      else if (stmt.children && stmt.children.blockSection) {
        const section = stmt.children.blockSection[0];
        if (section && section.children) {
          const sectionName = section.children.Name ? section.children.Name[0].image : '';
          const entries = [];
          
          // Process section entries
          if (section.children.blockEntry) {
            section.children.blockEntry.forEach(entry => {
              if (typeof entry === 'string' || typeof entry === 'number') {
                entries.push(entry);
              } else if (entry.image) {
                entries.push(entry.image);
              }
            });
          }
          
          // Add section to AST
          ast.sections.push({
            type: 'section',
            name: sectionName,
            entries: entries
          });
        }
      }
    });
  }
  
  return ast;
}

/**
 * Process a statement entry
 * @param {Object} entry - Statement entry from CST
 * @returns {Object} Processed entry
 */
function processStatementEntry(entry) {
  if (!entry || !entry.children) return null;
  
  // Process key-value pairs
  if (entry.children.keyValue) {
    const keyValue = entry.children.keyValue[0];
    if (keyValue && keyValue.children) {
      let key = '';
      
      // Get key
      if (keyValue.children.Name) {
        key = keyValue.children.Name[0].image;
      } else {
        // Try to get key from reserved keywords
        Object.keys(keyValue.children).forEach(tokenType => {
          if (tokenType !== 'value' && tokenType !== 'Semi') {
            key = keyValue.children[tokenType][0].image;
          }
        });
      }
      
      // Get value
      let value = null;
      if (keyValue.children.value) {
        value = processValue(keyValue.children.value[0]);
      }
      
      // Return key-value
      if (key) {
        return { key, value };
      }
    }
  }
  
  return null;
}

/**
 * Process a value from CST
 * @param {Object} valueNode - Value node from CST
 * @returns {*} Processed value
 */
function processValue(valueNode) {
  if (!valueNode || !valueNode.children) return null;
  
  // Process dictionary
  if (valueNode.children.dictionary) {
    const dict = valueNode.children.dictionary[0];
    if (dict && dict.children) {
      let dictName = '';
      
      // Get dictionary name
      if (dict.children.FoamFile) {
        dictName = dict.children.FoamFile[0].image;
      } else if (dict.children.Name) {
        dictName = dict.children.Name[0].image
      }
      
      // Process dictionary entries
      const entries = [];
      if (dict.children.statement) {
        dict.children.statement.forEach(entry => {
          const processedEntry = processStatementEntry(entry);
          if (processedEntry) {
            entries.push(processedEntry);
          }
        });
      }
      
      // Return dictionary
      return {
        type: 'dictionary',
        name: dictName,
        entries: entries
      };
    }
  }
  
  // Process array
  if (valueNode.children.array) {
    const array = valueNode.children.array[0];
    if (array && array.children) {
      const items = [];
      
      if (array.children.value) {
        array.children.value.forEach(item => {
          items.push(processValue(item));
        });
      }
      
      return {
        type: 'array',
        items: items
      };
    }
  }
  
  // Process tuple
  if (valueNode.children.tuple) {
    const tuple = valueNode.children.tuple[0];
    if (tuple && tuple.children) {
      const tupleName = tuple.children.Name ? tuple.children.Name[0].image : '';
      const items = [];
      
      if (tuple.children.tupleItem) {
        tuple.children.tupleItem.forEach(item => {
          if (item.children) {
            if (item.children.IntegerLiteral) {
              items.push(Number(item.children.IntegerLiteral[0].image));
            } else if (item.children.FloatLiteral) {
              items.push(Number(item.children.FloatLiteral[0].image));
            } else if (item.children.StringLiteral) {
              items.push(item.children.StringLiteral[0].image.replace(/^"|"$/g, ''));
            } else if (item.children.Name) {
              items.push(item.children.Name[0].image);
            }
          }
        });
      }
      
      return {
        type: 'tuple',
        name: tupleName,
        items: items
      };
    }
  }
  
  // Process other types
  if (valueNode.children.IntegerLiteral) {
    return Number(valueNode.children.IntegerLiteral[0].image);
  } else if (valueNode.children.FloatLiteral) {
    return Number(valueNode.children.FloatLiteral[0].image);
  } else if (valueNode.children.StringLiteral) {
    return valueNode.children.StringLiteral[0].image.replace(/^"|"$/g, '');
  } else if (valueNode.children.BooleanLiteral) {
    return valueNode.children.BooleanLiteral[0].image === 'true';
  }
  
  return null;
}

/**
 * Convert top-level AST dictionary to Blockly block
 * @param {Object} dictAst - Dictionary AST node
 * @param {Document} dom - XML DOM
 * @returns {Element} Blockly block XML element
 */
function astTopLevelDictToBlock(dictAst, dom) {
  const block = dom.createElement('block');
  block.setAttribute('type', 'common_standalone_dict');
  
  // Add dictionary name field
  if (dictAst.name) {
    const nameField = dom.createElement('field');
    nameField.setAttribute('name', 'DICT_NAME');
    nameField.textContent = dictAst.name;
    block.appendChild(nameField);
  }
  
  // Add entries
  if (dictAst.entries && dictAst.entries.length > 0) {
    const entriesStatement = dom.createElement('statement');
    entriesStatement.setAttribute('name', 'ENTRIES');
    
    let previousBlock = null;
    dictAst.entries.forEach(entry => {
      const entryBlock = astEntryToBlock(entry, dom);
      
      if (previousBlock) {
        const nextElement = dom.createElement('next');
        nextElement.appendChild(entryBlock);
        previousBlock.appendChild(nextElement);
      } else {
        entriesStatement.appendChild(entryBlock);
      }
      
      previousBlock = entryBlock;
    });
    
    block.appendChild(entriesStatement);
  }
  
  return block;
}

/**
 * Convert OpenFOAM text to Blockly blocks
 * @param {string} text - OpenFOAM text
 * @param {string} dictName - Dictionary name
 * @param {Blockly.Workspace} workspace - Target Blockly workspace
 * @returns {boolean} Success flag
 */
export function convertTextToBlocks(text, dictName, workspace) {
  try {
    // Use the imported function from openfoam.text-to-blocks.js
    const rootBlock = convertOpenFoamToBlocks(text, dictName, workspace);
    return !!rootBlock; // Return true if a root block was created
  } catch (error) {
    console.error('Error converting text to blocks:', error);
    return false;
  }
}

/**
 * Simple fallback parser for OpenFOAM text
 * @param {string} text - OpenFOAM text
 * @returns {Object} AST object
 */
function parseOpenFoamToJsonSimple(text) {
  const ast = {
    entries: [],
    sections: [],
    tuples: []
  };
  
  // Remove comments and normalize whitespace
  const cleanText = text.replace(/\/\*[\s\S]*?\*\//g, '')
                        .replace(/\/\/.*$/gm, '')
                        .replace(/#.*$/gm, '')
                        .replace(/\n+/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim();
  
  // Simple regex to find tuples - handle whitespace and line breaks
  const tupleRegex = /(\w+)\s*\(\s*([\s\S]*?)\s*\)\s*;/g;
  let match;
  
  while ((match = tupleRegex.exec(cleanText)) !== null) {
    const tupleName = match[1];
    const tupleContent = match[2].trim();
    
    // Parse tuple items
    const items = [];
    if (tupleContent) {
      // Split by commas, but respect quotes
      const itemRegex = /"([^"]*)"|'([^']*)'|([^,\s]+)/g;
      let itemMatch;
      
      while ((itemMatch = itemRegex.exec(tupleContent)) !== null) {
        const value = itemMatch[1] || itemMatch[2] || itemMatch[3];
        if (value) {
          // Convert to number if possible
          if (!isNaN(value) && value.trim() !== '') {
            items.push(Number(value));
          } else {
            items.push(value);
          }
        }
      }
    }
    
    ast.tuples.push({
      type: 'tuple',
      name: tupleName,
      items: items
    });
  }
  
  return ast;
}
